/**
 * Reset password (PUBLIC) — opened from the emailed link: https://getabeam.com/reset-password?token=…
 * (or beam://reset-password?token=… on native). The user sets a new password; the backend verifies
 * the 1-hour token, revokes all prior sessions, and returns a session → we sign in and land home.
 *
 * Mirrors accept-invite. Design system only (Screen / Field / Button / Text); no @smartbox/*.
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSession } from '../src/auth/session';
import { ApiRequestError } from '../src/api/client';
import { Screen, Text, Field, Button } from '../src/components/ui';
import { BeamLogo } from '../src/components/brand/Logo';
import { theme } from '../src/theme';

export default function ResetPasswordScreen() {
  const params = useLocalSearchParams<{ token?: string }>();
  const token = typeof params.token === 'string' ? params.token : '';
  const resetPassword = useSession((s) => s.resetPassword);
  const router = useRouter();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (!token) return setError('This reset link is missing its token. Request a new one from the sign-in screen.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true);
    try {
      await resetPassword(token, password);
      // Signed in by the reset response — AuthGate routes from here.
      router.replace('/(app)/(tabs)/home');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Could not reset your password. The link may have expired.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.container}>
          <View style={styles.brand}>
            <BeamLogo size={40} />
            <Text variant="title">Set a new password</Text>
            <Text variant="subhead" color="secondary">
              Choose a new password for your account. This signs you out of all other devices.
            </Text>
          </View>

          <View style={styles.form}>
            <Field
              label="New password"
              value={password}
              onChangeText={setPassword}
              secureToggle
              autoCapitalize="none"
              textContentType="newPassword"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              editable={!busy}
              accessibilityLabel="New password"
              returnKeyType="next"
            />

            <Field
              label="Confirm password"
              value={confirm}
              onChangeText={setConfirm}
              secureToggle
              autoCapitalize="none"
              textContentType="newPassword"
              autoComplete="new-password"
              placeholder="Repeat password"
              editable={!busy}
              accessibilityLabel="Confirm password"
              returnKeyType="done"
              onSubmitEditing={onSubmit}
              error={error ?? undefined}
            />

            <Button
              title="Set password & sign in"
              onPress={onSubmit}
              loading={busy}
              disabled={busy}
              size="lg"
              style={styles.submit}
              accessibilityLabel="Set password and sign in"
            />

            <Button
              title="Back to sign in"
              variant="ghost"
              size="sm"
              onPress={() => router.replace('/login')}
              accessibilityLabel="Go to sign in"
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
});
